import { CopyOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Button, message, Modal, Tooltip } from 'antd';
import FormField, { FormFieldProps } from 'components/FormField';
import StatusButton from 'components/StatusButton';
import { getSegmentsLength, hasConcept } from 'components/TemplateEditor/Editor';
import useModal from 'hooks/useModal';
import useSmartField from 'hooks/useSmartField';
import { useState } from 'react';
import { unstable_batchedUpdates as batchedUpdates } from 'react-dom';
import { useTranslate } from '@tolgee/react';
import { Element } from 'react-scroll';
import { platformExecuteContentLimitLengthMap } from 'services/config/platform';
import { regionLanguageMap } from 'services/config/region';
import toLowerCase from 'utils/toLowerCase';
import { What } from 'views/Marketing/components/PolicyTable';
import { lazadaUnLimitMessageCountScenarios } from '../../config';
import ExecuteContextsFieldV2, { getMsgLength } from '../ExecuteContextsFieldV2';
import { getSlateExecuteContext, useLangTemplatesMap, useMsgTypes } from '../share';
import { FV } from '../../NewPolicy/share';
import {
  useGetConcepts,
  ExtraTabs,
  translateTemplate,
  recommendTemplatesNew2Old,
} from '../TranslateTool';
import { useFormikContext } from 'formik';
import { useGetRegionLabel } from 'hooks/business/useRegion';
import { useReactScrollContext } from 'views/Aide/components/ReactScrollContext';
import { removeParagraph, removeSticker } from 'components/TemplateEditor/utils/remove';

export const TaskWrapper = styled.div`
  background: #fff;
  margin-bottom: 16px;
  display: flex;
`;

export const TaskLeftWrap = styled.div`
  margin-right: 24px;
  border-right: 1px solid #eee;
  flex: 1 0 0;
`;

export const EditorWrap = styled.div`
  display: flex;
  > div {
    flex: 1 0 0;
    margin-right: 24px;
  }
`;

export const TitleLine = styled.div`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 12px;
  span {
    margin: 0 2px;
  }
`;

export const LeadingLine = styled.b`
  border-left: #3370ff solid 4px;
  height: 14px;
  display: inline-block;
  font-size: 10px;
  margin-right: 8px;
`;

export interface RegionExecuteContextsFieldProps {
  scenarioType: Marketing.AllScenarioType;
  platform: General.PlatformUpperCase;
  disabled?: boolean;
  validate?: FormFieldProps<Required<FV>['regionExecuteContexts'][number]>['validate'];
  replyOption?: Marketing.ReplyOption;
}

function RegionExecuteContextsField(props: RegionExecuteContextsFieldProps) {
  const { scenarioType, platform, disabled = false, replyOption } = props;

  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();

  const { createRegionContextId } = useReactScrollContext();

  const langTemplatesMap = useLangTemplatesMap({
    scenarioType,
    round: 1,
    platform,
  });

  const {
    field: { value: regionExecuteContexts = [], onChange: onRegionExecuteContextsChange },
  } = useSmartField<FV['regionExecuteContexts']>('regionExecuteContexts');

  const {
    values: { recommendTemplates, knowledgeId, contentItemList },
  } = useFormikContext<FV>();

  const copyModal = useModal();

  const [refresh, setRefresh] = useState(1);

  const concepts = useGetConcepts(platform, scenarioType, replyOption);

  const msgTypes = useMsgTypes(platform, scenarioType);

  const sendContentTips = platform === 'LAZADA' &&
    !lazadaUnLimitMessageCountScenarios.includes(scenarioType) && (
      <Tooltip
        title={t(
          'aide.因Lazada平台限制，买家不回复消息最多允许店铺发送5条/天消息。策略中的发送内容最多可添加5条。',
        )}
      >
        <What />
      </Tooltip>
    );

  const list = regionExecuteContexts.map(({ enable = true, region, executeContext }, index) => {
    if (!enable) {
      return null;
    }

    const { [toLowerCase(region)]: regionLang } = regionLanguageMap;

    const newRecommendTemplates = langTemplatesMap?.[regionLang === 'tl' ? 'ph' : regionLang];

    const getRecommendTemplates = () =>
      recommendTemplatesNew2Old(newRecommendTemplates) || recommendTemplates;

    const copy = !disabled &&
      regionExecuteContexts.filter(({ enable = true }) => enable).length > 1 &&
      regionExecuteContexts.findIndex(({ enable = true }) => enable) === index && (
        <>
          <Button
            type="link"
            style={{ paddingRight: 0 }}
            disabled={getSegmentsLength(executeContext.content, 'text') === 0}
            onClick={copyModal.show}
          >
            <CopyOutlined />
            {t('aide.复用内容至其他全部站点')}
          </Button>
          <Modal
            title={t('aide.提示')}
            visible={copyModal.visible}
            footer={[
              <Button
                key="back"
                onClick={() => {
                  const nextExecuteContext = {
                    content: executeContext.content.filter(removeSticker),
                    translatedContent: executeContext.translatedContent
                      .filter(removeSticker)
                      .filter(removeParagraph),
                  };

                  const next = regionExecuteContexts.map((context, currentIndex) => {
                    if (currentIndex === index) {
                      return context;
                    }

                    return {
                      ...context,
                      executeContext: nextExecuteContext,
                    };
                  });

                  onRegionExecuteContextsChange(next);

                  setRefresh((_) => _ + 1);

                  copyModal.hide();
                }}
              >
                {t('aide.不需要翻译')}
              </Button>,
              <StatusButton
                key="submit"
                type="primary"
                onClick={() => {
                  const content = executeContext.content.filter(removeSticker);

                  return regionExecuteContexts
                    .reduce<Promise<Required<FV>['regionExecuteContexts']>>(
                      (res, cur, currentIndex) => {
                        return res.then((list) => {
                          if (currentIndex === index) {
                            list.push(cur);

                            return list;
                          }

                          const { enable, region } = cur;

                          const { [toLowerCase(region)]: language } = regionLanguageMap;

                          return translateTemplate(
                            content,
                            language,
                            getRecommendTemplates(),
                            'GOOGLE',
                            {
                              from: 'regions',
                            },
                          ).then((content) => {
                            list.push({
                              enable,
                              region,
                              executeContext: {
                                content,
                                translatedContent: [],
                              },
                            });

                            return list;
                          });
                        });
                      },
                      Promise.resolve([]),
                    )
                    .then((res) => {
                      batchedUpdates(() => {
                        onRegionExecuteContextsChange(res);

                        setRefresh((_) => _ + 1);

                        copyModal.hide();
                      });
                    })
                    .catch(() => {
                      message.error(t('translation.操作失败，请稍后再试'));
                    });
                }}
              >
                {t('aide.需要翻译')}
              </StatusButton>,
            ]}
            onCancel={copyModal.hide}
          >
            {t('aide.复制任务提示')}
          </Modal>
        </>
      );

    const title = (
      <TitleLine
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          paddingRight: '24px',
        }}
      >
        <div>
          <LeadingLine />
          {t('aide.发送内容')}
          <span>-</span>
          {getRegionLabel(region)}
          {sendContentTips}
        </div>
        {copy}
      </TitleLine>
    );

    const {
      validate = (value) => {
        if (!value) {
          return t('refundSetting.请输入回复内容');
        }

        const {
          enable,
          executeContext: { content, translatedContent },
        } = value;

        if (!enable) {
          return;
        }

        if (platform === 'LAZADA' && !lazadaUnLimitMessageCountScenarios.includes(scenarioType)) {
          const msgLength = getMsgLength(content);

          if (msgLength > 5) {
            return t('aide.发送消息不可超过 5 条');
          }
        }

        const hasText = content.some((v) => v.type === 'paragraph');
        if (hasText) {
          const contentLength = getSegmentsLength(content, 'text');
          const translatedContentLength = getSegmentsLength(translatedContent, 'text');
          const { [platform]: contentLimit } = platformExecuteContentLimitLengthMap;
          if (contentLength === 0) {
            return t('refundSetting.请输入回复内容');
          }

          if (contentLength > contentLimit || translatedContentLength > contentLimit) {
            return t('refundSetting.回复内容不可超出x字', { x: contentLimit });
          }

          if (scenarioType === 'RECEPTION_SCORE' && replyOption === 'DEFAULT') {
            if (!hasConcept(content, '好评') || !hasConcept(content, '差评')) {
              return t('aide.回复内容需包含“好评”和“差评”参数');
            }
          }
        } else {
          if (scenarioType === 'RECEPTION_SCORE' && replyOption === 'DEFAULT') {
            return t('aide.请添加文本类型回复');
          }

          const contentLength = getSegmentsLength(content, 'image');
          if (contentLength === 0) {
            return t('refundSetting.请输入回复内容');
          }
        }

        return undefined;
      },
    } = props;

    const formField = (
      <FormField<Required<FV>['regionExecuteContexts'][number]>
        name={`regionExecuteContexts.${index}`}
        validate={validate}
      >
        {({ field: { value, onChange, onBlur }, meta: { touched } }) => {
          const editor = (
            <ExecuteContextsFieldV2
              scenarioType={scenarioType}
              platform={platform}
              region={region}
              msgTypes={msgTypes}
              value={value.executeContext}
              onChange={(executeContext) => {
                onChange({
                  ...value,
                  executeContext,
                });

                setTimeout(() => {
                  if (!touched) {
                    onBlur();
                  }
                });
              }}
              error=""
              refresh={refresh}
              recommendTemplates={recommendTemplates}
              langTemplatesMap={langTemplatesMap}
              replyOption={replyOption}
              isKnowledge={Boolean(knowledgeId)}
              contentItemList={contentItemList}
            />
          );

          return (
            <EditorWrap>
              <div style={disabled ? { pointerEvents: 'none', cursor: 'not-allowed' } : {}}>
                {editor}
              </div>
            </EditorWrap>
          );
        }}
      </FormField>
    );

    return (
      <Element key={region} name={createRegionContextId(region)}>
        <TaskWrapper>
          <TaskLeftWrap>
            {title}
            {formField}
          </TaskLeftWrap>
          <ExtraTabs
            platform={platform}
            region={region}
            concepts={concepts}
            allowDividing
            recommendTemplates={recommendTemplates}
            newRecommendTemplates={newRecommendTemplates}
            systemContent={
              scenarioType === 'RECEPTION_SCORE' && replyOption === 'DEFAULT'
                ? t('aide.买家咨询结束后，自动发起接待评分邀请')
                : undefined
            }
            query={(() => {
              if (scenarioType !== 'RECEPTION_SCORE') {
                return undefined;
              }

              if (replyOption === '1') {
                return getSlateExecuteContext({
                  content: '#mustache#{{#text}}1{{/text}}',
                  translatedContent: '#mustache#{{#text}}1{{/text}}',
                });
              }

              if (replyOption === '2') {
                return getSlateExecuteContext({
                  content: '#mustache#{{#text}}2{{/text}}',
                  translatedContent: '#mustache#{{#text}}2{{/text}}',
                });
              }

              return undefined;
            })()}
            value={executeContext}
            onChange={
              disabled
                ? undefined
                : (value) =>
                    onRegionExecuteContextsChange([
                      ...regionExecuteContexts.slice(0, index),
                      { ...regionExecuteContexts[index], executeContext: value },
                      ...regionExecuteContexts.slice(index + 1),
                    ])
            }
          />
        </TaskWrapper>
      </Element>
    );
  });

  return <>{list}</>;
}

export default RegionExecuteContextsField;
